import React, { useEffect, useState } from 'react';
import { Plus, FileText, DollarSign, StopCircle, Clock } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const duration = (startDate) => {
  const start = new Date(startDate);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  let days = now.getDate() - start.getDate();
  if (days < 0) { months -= 1; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
  if (months < 0) { years -= 1; months += 12; }
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}m`);
  if (days > 0 || parts.length === 0) parts.push(`${days}d`);
  return parts.join(' ');
};

export default function Tenancies() {
  const [tenancies, setTenancies] = useState([]);
  const [students, setStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(null);
  const [form, setForm] = useState({ studentId: '', roomId: '', startDate: '', monthlyRent: '', keyMoney: '', keyMoneyMonths: 2 });
  const [payForm, setPayForm] = useState({ amount: '', type: 'RENT', month: '', notes: '' });

  const load = () => api.get('/tenancies').then(r => setTenancies(r.data)).catch(console.error);

  useEffect(() => {
    load();
    api.get('/students').then(r => setStudents(r.data));
    api.get('/rooms').then(r => setRooms(r.data.filter(r => r.status !== 'OCCUPIED' || true)));
  }, []);

  const submit = async e => {
    e.preventDefault();
    try {
      await api.post('/tenancies', form);
      toast.success('Tenancy created');
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const addPayment = async e => {
    e.preventDefault();
    try {
      await api.post(`/tenancies/${showPayment}/payments`, payForm);
      toast.success('Payment recorded');
      setShowPayment(null);
      setPayForm({ amount: '', type: 'RENT', month: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const endTenancy = async () => {
    try {
      await api.put(`/tenancies/${confirmEnd.id}/end`);
      toast.success('Tenancy ended');
      setConfirmEnd(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
      setConfirmEnd(null);
    }
  };

  const generateAgreement = async tenancyId => {
    try {
      const { data } = await api.post(`/agreements/tenancy/${tenancyId}/generate`);
      toast.success('Agreement generated — downloading...');
      const res = await api.get(`/agreements/download/${data.agreement.id}`, { responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `agreement-${tenancyId}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Tenancies</h2>
        <button onClick={() => setShowForm(true)} className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Plus size={16} /> New Tenancy
        </button>
      </div>

      <div className="space-y-3">
        {tenancies.map(t => (
          <div key={t.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-800">{t.student.firstName} {t.student.lastName}</p>
                <p className="text-xs text-purple-600 font-mono">{t.student.registrationNo}</p>
                <p className="text-sm text-gray-500 mt-1">Room {t.room.roomNumber} · Rs. {Number(t.monthlyRent).toLocaleString()}/mo</p>
                <p className="text-xs text-gray-400">Key Money: Rs. {Number(t.keyMoney).toLocaleString()}</p>
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                  <Clock size={11} />
                  Since {new Date(t.startDate).toLocaleDateString()} &nbsp;·&nbsp;
                  <span className="font-medium text-purple-600">{duration(t.startDate)}</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{t.status}</span>
                {t.status === 'ACTIVE' && (
                  <>
                    <button onClick={() => setShowPayment(t.id)} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-100">
                      <DollarSign size={12} /> Payment
                    </button>
                    <button onClick={() => generateAgreement(t.id)} className="flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-3 py-1 rounded-full hover:bg-purple-100">
                      <FileText size={12} /> Agreement
                    </button>
                    <button onClick={() => setConfirmEnd({ id: t.id, name: `${t.student.firstName} ${t.student.lastName}`, room: t.room.roomNumber })} className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-3 py-1 rounded-full hover:bg-red-100">
                      <StopCircle size={12} /> End
                    </button>
                  </>
                )}
              </div>
            </div>
            {t.payments?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-50">
                <p className="text-xs font-medium text-gray-600 mb-2">Recent Payments</p>
                <div className="flex flex-wrap gap-2">
                  {t.payments.slice(0, 3).map(p => (
                    <div key={p.id} className="text-xs bg-gray-50 rounded px-2 py-1">
                      <span className="font-medium">Rs. {Number(p.amount).toLocaleString()}</span> · {p.type} {p.month && `(${p.month})`} · {new Date(p.paymentDate).toLocaleDateString()}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {tenancies.length === 0 && <div className="text-center text-gray-400 py-16">No tenancies found</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-4">New Tenancy</h3>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Student *</label>
                <select required value={form.studentId} onChange={e => setForm(p => ({ ...p, studentId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="">Select student</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.registrationNo})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Room *</label>
                <select required value={form.roomId} onChange={e => { const r = rooms.find(r => r.id === Number(e.target.value)); setForm(p => ({ ...p, roomId: e.target.value, monthlyRent: r?.monthlyRent || '' })); }}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="">Select room</option>
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.roomNumber} - {r.category} ({r.status})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Start Date *</label>
                <input type="date" required value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Monthly Rent *</label>
                  <input type="number" required value={form.monthlyRent} onChange={e => setForm(p => ({ ...p, monthlyRent: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Key Money *</label>
                  <input type="number" required value={form.keyMoney} onChange={e => setForm(p => ({ ...p, keyMoney: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Key Money Months</label>
                <input type="number" min={1} value={form.keyMoneyMonths} onChange={e => setForm(p => ({ ...p, keyMoneyMonths: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-purple-700 text-white py-2 rounded-lg text-sm">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg mb-4">Record Payment</h3>
            <form onSubmit={addPayment} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Amount (Rs.) *</label>
                <input type="number" required value={payForm.amount} onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Type *</label>
                <select value={payForm.type} onChange={e => setPayForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="RENT">Rent</option>
                  <option value="KEY_MONEY">Key Money</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Month (e.g. 2024-01)</label>
                <input type="month" value={payForm.month} onChange={e => setPayForm(p => ({ ...p, month: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <input value={payForm.notes} onChange={e => setPayForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowPayment(null)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-purple-700 text-white py-2 rounded-lg text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmEnd}
        title="End Tenancy"
        message={confirmEnd ? `Are you sure you want to end the tenancy for ${confirmEnd.name} in Room ${confirmEnd.room}? This will mark the room as available.` : ''}
        confirmLabel="Yes, End Tenancy"
        onConfirm={endTenancy}
        onCancel={() => setConfirmEnd(null)}
      />
    </div>
  );
}
